import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import RemoveRedEyeOutlined from "@mui/icons-material/RemoveRedEyeOutlined";
import UploadFile from "@mui/icons-material/UploadFile";
import RestartAlt from "@mui/icons-material/RestartAlt";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { colors } from "healthex-common/colors";
import { TranscodeStatus } from "healthex-common/transcode.dtos";
import { observer } from "mobx-react";
import * as React from "react";
import { VisuallyHiddenInput } from "./common.styles";
import { getFilePreviewInfo, PDFPreviewDialog, VideoPreviewDialog } from "./FilePreview";
import { ButtonAndDropText, DragLeaveDetector, ErrorIcon, ErrorMessage, ErrorTryAgain, FileButtons, FileDot, FileName, FileNameIcon, FileUploadDashedContainer, FileUploadMessageContainer, FileUploadParent, LearnMoreText, PdfIcon, PreviewImage, PreviewImageWrapper, TypeAndSize, UploadInput, UploadPreview, UploadTipsContainer, VideoHintLink, FileUploadVideoCircularProgress, FileUploadVideoCheckCircleOutlinedIcon, FileUploadVideoText, FileUploadingContainer, FileUploadingProgressContainer, FileUploadingCircularProgress, FileUploadingProgressText, } from "./FileUploadView.styles";
import { LighterText, Text } from "./HXDesktopPage.styles";
import { urlWithoutQueryParams } from "healthex-common/utils";
import { fontWeights } from "healthex-common/weights";
function preventDefault(e) {
    e.preventDefault();
}
export const acceptImageTypes = ["png", "jpeg", "gif", "webp"];
export const acceptImageMimeTypes = "image/png, image/jpeg, image/gif, image/webp";
export const acceptVideoTypes = ["mp4", "mkv", "mov", "avi", "wmv"];
export const acceptVideoMimeTypes = "video/mp4, video/*,.mkv, video/quicktime, video/x-msvideo, video/x-ms-wmv, video/avi";
export const acceptCSVMimeTypes = "application/csv, application/x-csv, text/csv, text/comma-separated-values, text/x-comma-separated-values, text/tab-separated-values";
export const FileUploadView = observer((props) => {
    var _a;
    React.useEffect(() => {
        window.addEventListener("dragover", preventDefault, false);
        window.addEventListener("drop", preventDefault, false);
        return () => {
            window.removeEventListener("dragover", preventDefault, false);
            window.removeEventListener("drop", preventDefault, false);
        };
    });
    const [fileHovering, setFileHovering] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [preview, setPreview] = React.useState(false);
    const isUploading = !!props.uploadingInfo && props.uploadingInfo.progress !== undefined && props.uploadingInfo.progress !== null;
    let videoTranscodeStatus = null;
    let isLocalVideo = false;
    let isLocalVideoTranscoding = false;
    if (props.file !== null && typeof props.file === "object" && "status" in props.file) {
        videoTranscodeStatus = props.file.status;
        isLocalVideo = urlWithoutQueryParams((_a = props.uploadingInfo) === null || _a === void 0 ? void 0 : _a.signedUrl) === urlWithoutQueryParams(props.file.sourceUrl);
        isLocalVideoTranscoding =
            isLocalVideo && (videoTranscodeStatus === TranscodeStatus.UPLOADING || videoTranscodeStatus === TranscodeStatus.TRANSCODING);
    }
    const showPreviewOrUploadingUI = !error && (props.file !== null || !!props.existingFileUrl);
    return (_jsxs(FileUploadParent, { children: [_jsxs(FileUploadDashedContainer, { className: props.className, fileHovering: fileHovering, error: !!error, requiredError: !!props.requiredError, onDrop: handleDrop, onDragOver: handleDragOver, onDragEnter: handleDragEnter, height: props.uploadAreaHeight, children: [fileHovering && _jsx(DragLeaveDetector, { onDragLeave: handleDragLeave }), isUploading && showPreviewOrUploadingUI ? (_jsx(FileUploadingContainer, { children: fileUploading() })) : showPreviewOrUploadingUI ? (_jsx(UploadPreview, { disabled: props.disabled, children: filePreview() })) : (_jsxs(UploadInput, { children: [!error ? (_jsxs(ButtonAndDropText, { children: [_jsxs(Button, { variant: "outlined", component: "label", startIcon: _jsx(UploadFile, {}), disabled: props.disabled, children: [buttonText(), hiddenFileUpload()] }), _jsx(Text, { disabled: props.disabled, children: "or drag and drop" })] })) : (_jsxs(_Fragment, { children: [_jsx(ErrorIcon, {}), _jsxs(ErrorTryAgain, { children: [_jsx(ErrorMessage, { children: error }), _jsxs(Button, { variant: "text", component: "label", color: "primary", size: "medium", children: ["Try again", hiddenFileUpload("try-again")] })] })] })), _jsxs(TypeAndSize, { disabled: props.disabled, children: [_jsx("div", { children: humanReadableJoin() }), _jsx("div", { children: "\u2022" }), _jsx("div", { children: sizeLimitText() })] })] }))] }), renderBottomVideoMessage()] }));
    function renderBottomVideoMessage() {
        if (props.accept !== "video" || props.disabled)
            return null;
        const defaultMessage = isLocalVideo
            ? "Video uploads may take some time to process. Please allow a few minutes for it to complete."
            : "Video uploads may take some time to process. If you attempt to preview the video before it's fully uploaded, it may not be available yet. Please allow a few minutes for the upload to complete.";
        let message = null;
        let icon = null;
        if (!isUploading &&
            !isLocalVideo &&
            (videoTranscodeStatus === TranscodeStatus.UPLOADING || videoTranscodeStatus === TranscodeStatus.TRANSCODING)) {
            message = defaultMessage;
            icon = _jsx(FileUploadVideoCircularProgress, {});
        }
        else if (videoTranscodeStatus === TranscodeStatus.FAILED) {
            message = isLocalVideo
                ? "We couldn’t process your video. Try again or delete the video and upload a new one."
                : "Upload failed. Try again or delete the video and upload a new one.";
        }
        else if (isLocalVideo && videoTranscodeStatus === TranscodeStatus.COMPLETED) {
            message = "Your video is ready to preview!";
            icon = _jsx(FileUploadVideoCheckCircleOutlinedIcon, {});
        }
        else if (props.showDefaultVideoMessage) {
            message = defaultMessage;
        }
        if (!message)
            return null;
        return (_jsxs(FileUploadMessageContainer, { children: [icon && icon, _jsx(FileUploadVideoText, { isLocalVideo: isLocalVideo, status: videoTranscodeStatus, disabled: props.disabled, children: message })] }));
    }
    function buttonText() {
        if (props.label)
            props.label;
        return props.accept === "video" ? "Upload video" : "Upload file";
    }
    function sizeLimitText() {
        if (props.sizeLimitMB >= 1000)
            return `${props.sizeLimitMB / 1000}GB max`;
        return `${props.sizeLimitMB}MB max`;
    }
    function humanReadableJoin() {
        if (props.accept === "video")
            return ".mp4 (recommended), .mkv, .mov, .avi, .wmv";
        if (props.accept === "csv")
            return "CSV only";
        const items = props.accept.map((item) => item.toUpperCase());
        let text = "";
        if (items.length === 0)
            return (text = "");
        else if (items.length === 1)
            text = items[0];
        else if (items.length === 2)
            text = `${items[0]} or ${items[1]}`;
        else {
            text = `${items.slice(0, -1).join(", ")}, or ${items[items.length - 1]}`;
        }
        return `${text} only`;
    }
    function fileIcon(ext) {
        if (props.accept === "video")
            return _jsx(VideoFileIcon, { disabled: props.disabled });
        if (typeof ext === "string") {
            if (ext === "pdf") {
                return _jsx(PdfIcon, { disabled: props.disabled });
            }
        }
        return _jsx(FileNameIcon, { disabled: props.disabled });
    }
    function fileUploading() {
        var _a, _b;
        let filetype;
        if (props.accept === "video") {
            filetype = "VIDEO";
        }
        else if (props.accept.includes("pdf")) {
            filetype = "PDF";
        }
        else {
            filetype = "IMAGE";
        }
        const { filename } = getFilePreviewInfo(props.file, filetype, props.existingFileUrl, props.canPreviewVideoBlob);
        return (_jsxs(_Fragment, { children: [_jsx(FileUploadProgress, { progress: (_b = (_a = props.uploadingInfo) === null || _a === void 0 ? void 0 : _a.progress) !== null && _b !== void 0 ? _b : 0, disabled: props.disabled }), _jsxs(LighterText, { children: ["uploading ", filename, "..."] })] }));
    }
    function filePreview() {
        var _a;
        let filetype;
        if (props.accept === "video") {
            filetype = "VIDEO";
        }
        else if (props.accept === "csv") {
            filetype = "CSV";
        }
        else if (props.accept.includes("pdf")) {
            filetype = "PDF";
        }
        else {
            filetype = "IMAGE";
        }
        const { filename, url, fileExt, previewable, videoUrlType } = getFilePreviewInfo(props.file, filetype, props.existingFileUrl, props.canPreviewVideoBlob);
        const showPreviewButton = filetype === "PDF" || filetype === "VIDEO";
        const showTryAgainButton = videoTranscodeStatus === TranscodeStatus.FAILED && !!props.onDelete;
        if (filetype === "IMAGE" && previewable) {
            return (_jsxs(_Fragment, { children: [_jsx(PreviewImageWrapper, { disabled: props.disabled, style: { cursor: props.disabled ? "default" : props.onViewImage ? "pointer" : "default" }, onClick: () => !props.disabled && props.onViewImage && props.onViewImage(url, filename), children: _jsx(PreviewImage, { src: url }) }), _jsxs(IconButton, { component: "label", disabled: props.disabled, children: [_jsx(Edit, {}), hiddenFileUpload("edit")] })] }));
        }
        else {
            return (_jsxs(_Fragment, { children: [_jsxs(FileName, { children: [fileIcon(fileExt), filename] }), _jsxs(FileButtons, { children: [isLocalVideoTranscoding ? (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "text", component: "label", color: "primary", size: "medium", startIcon: _jsx(FileUploadVideoCircularProgress, { disabled: true }), disabled: true, sx: { fontWeight: fontWeights.SEMI_BOLD }, children: "Processing your video..." }), _jsx(FileDot, { children: "\u2022" })] })) : showTryAgainButton ? (_jsxs(_Fragment, { children: [_jsxs(Button, { variant: "text", component: "label", color: "primary", size: "medium", startIcon: _jsx(RestartAlt, {}), disabled: props.disabled, children: ["Try again", hiddenFileUpload("try-again")] }), _jsx(FileDot, { children: "\u2022" })] })) : showPreviewButton ? (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "text", component: "label", color: "primary", size: "medium", startIcon: _jsx(RemoveRedEyeOutlined, {}), disabled: !previewable || props.disabled, onClick: () => setPreview(true), children: "Preview" }), _jsx(FileDot, { children: "\u2022" })] })) : (_jsx(_Fragment, {})), filetype === "VIDEO" ? (_jsx(Button, { variant: "text", component: "label", color: "error", size: "medium", startIcon: _jsx(DeleteOutline, {}), disabled: (_a = props.disableDelete) !== null && _a !== void 0 ? _a : props.disabled, onClick: () => { var _a; return (_a = props.onDelete) === null || _a === void 0 ? void 0 : _a.call(props); }, children: "Delete" })) : (_jsxs(Button, { variant: "text", component: "label", color: "error", size: "medium", startIcon: _jsx(UploadFile, {}), disabled: props.disabled, children: ["Replace", hiddenFileUpload("replace")] }))] }), filetype === "PDF" && (_jsx(PDFPreviewDialog, { open: preview, onClose: () => setPreview(false), filename: filename, url: url })), filetype === "VIDEO" && (_jsx(VideoPreviewDialog, { open: preview, onClose: () => setPreview(false), filename: filename, url: url, videoUrlType: videoUrlType }))] }));
        }
    }
    function getAcceptTypes() {
        if (props.accept === "video")
            return acceptVideoMimeTypes;
        if (props.accept === "csv")
            return acceptCSVMimeTypes;
        return props.accept.map((ext) => getAcceptType(ext)).join(",");
    }
    function getAcceptType(ext) {
        if (ext === "pdf") {
            return "application/pdf";
        }
        else if (ext === "jpg") {
            return "image/jpeg";
        }
        else {
            return `image/${ext}`;
        }
    }
    function hiddenFileUpload(buttonType = "main") {
        return (_jsx(VisuallyHiddenInput, { type: "file", accept: getAcceptTypes(), onClick: (e) => e.stopPropagation(), onChange: (e) => {
                var _a, _b;
                const file = (_b = (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null;
                if (file) {
                    validateAndSelectFile(file);
                }
                else {
                    console.log("No file selected in onChange handler");
                }
                e.target.value = "";
            } }, `file-input-${buttonType}`));
    }
    function handleDrop(event) {
        var _a, _b;
        event.preventDefault();
        setFileHovering(false);
        const file = (_b = (_a = [...event.dataTransfer.items].find((item) => item.kind === "file")) === null || _a === void 0 ? void 0 : _a.getAsFile()) !== null && _b !== void 0 ? _b : null;
        validateAndSelectFile(file);
    }
    function handleDragOver(event) {
        event.stopPropagation();
        event.preventDefault();
    }
    function handleDragEnter() {
        setFileHovering(true);
    }
    function handleDragLeave() {
        setFileHovering(false);
    }
    function validateAndSelectFile(file) {
        var _a;
        if (!file)
            return;
        let fileExt = (_a = file.name.split(".").pop()) !== null && _a !== void 0 ? _a : "";
        if (fileExt)
            fileExt = fileExt.toLowerCase().trim();
        const hasValidFileType = !!file.type && getAcceptTypes().toLowerCase().includes(file.type);
        const isValidMkvExtension = props.accept === "video" && file.type === "" && fileExt === "mkv";
        if (!(hasValidFileType || isValidMkvExtension)) {
            setError("File type not supported.");
        }
        else if (file.size > props.sizeLimitMB * 1000 * 1000) {
            setError("File is too large.");
        }
        else {
            setError(null);
            props.onFileSelected(file);
        }
    }
});
export const LogoUploadTips = (props) => {
    const { color } = props;
    return (_jsxs(UploadTipsContainer, { color: color, children: [_jsx(Text, { color: color, children: "Some tips on choosing a good logo:" }), _jsxs("ul", { children: [_jsx("li", { children: _jsx(Text, { color: color, children: "Use a logo with a transparent background" }) }), _jsx("li", { children: _jsx(Text, { color: color, children: "Ensure the logo looks good on a white or light background" }) }), _jsx("li", { children: _jsx(Text, { color: color, children: "Choose a square or landscape-oriented logo" }) }), _jsx("li", { children: _jsx(Text, { color: color, children: "Upload an image that is 64px tall or larger" }) })] })] }));
};
export const LogoIconUploadTips = (props) => {
    const { color } = props;
    return (_jsxs(UploadTipsContainer, { color: color, children: [_jsx(Text, { color: color, children: "Here are some tips on choosing a good icon:" }), _jsxs("ul", { children: [_jsx("li", { children: _jsx(Text, { color: color, children: "Choose a square or circular image with limited text" }) }), _jsx("li", { children: _jsx(Text, { color: color, children: "Upload an image that is 64px tall or larger" }) })] })] }));
};
export const RectangularLogoUploadTips = (props) => {
    const { color } = props;
    return (_jsxs(UploadTipsContainer, { color: color, children: [_jsx(Text, { color: color, children: "Here are some tips on choosing a good logo:" }), _jsx("ul", { children: _jsx("li", { children: _jsx(Text, { color: color, children: "Choose a rectangular image, ideally 402 x 185 pixels" }) }) })] }));
};
export const LightLogoUploadTips = () => {
    return _jsx(LogoUploadTips, { color: colors.black_light });
};
export const VideoUploadTips = observer((props) => {
    const { disabled } = props;
    return (_jsxs(UploadTipsContainer, { disabled: disabled, children: [_jsx(Text, { disabled: disabled, children: "Here are some tips on choosing a good introduction video:" }), _jsxs("ul", { children: [_jsx("li", { children: _jsx(Text, { disabled: disabled, children: "Use a video that is high resolution (up to 720p)" }) }), _jsx("li", { children: _jsx(Text, { disabled: disabled, children: "Ensure the video has audio and/or captions so it is accessible to patients with different needs" }) }), _jsx("li", { children: _jsx(Text, { disabled: disabled, children: "Choose a video that is short and concise so patients stay engaged. Under 5 minutes long is ideal" }) }), _jsx("li", { children: _jsxs(Text, { disabled: disabled, children: ["If the file is too big, consider using third-party apps to compress files.", " ", _jsx(VideoHintLink, { disabled: disabled, onClick: () => props.onLearnMore(), children: "Learn more" })] }) })] })] }));
});
const VideoFileIcon = observer((props) => {
    const color = props.disabled ? colors.black_30 : colors.black_lighter;
    return (_jsx("svg", { width: "19", height: "24", viewBox: "0 0 19 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("g", { id: "Video file icon", children: _jsxs("g", { id: "Group", children: [_jsx("path", { id: "Vector", d: "M13.4074 13.9019L8.4662 10.4162C8.25091 10.2637 7.96785 10.2454 7.7342 10.3661C7.49985 10.4875 7.35303 10.7289 7.35303 10.9929V17.9649C7.35303 18.5367 8.00103 18.8706 8.4662 18.5416L13.4074 15.056C13.5951 14.924 13.706 14.7087 13.706 14.4793C13.706 14.2492 13.5951 14.0339 13.4074 13.9019ZM8.76479 16.6033V12.3546L11.7754 14.4786L8.76479 16.6033Z", fill: color }), _jsx("path", { id: "Vector_2", d: "M18.4409 7.62633L12.088 1.27339C11.6355 0.820921 10.3459 1.06657 1.70588 1.06657C1.31624 1.06657 1 1.3828 1 1.77245V22.9489C1 23.3386 1.31624 23.6548 1.70588 23.6548H17.9412C18.3308 23.6548 18.6471 23.3386 18.6471 22.9489C18.6471 8.35833 18.8539 8.03927 18.4409 7.62633ZM16.2372 7.41951H12.2948V3.47645L16.2372 7.41951ZM2.41176 22.243V2.47833H10.8831V8.12539C10.8831 8.51504 11.1993 8.83127 11.5889 8.83127H17.2353V22.243H2.41176Z", fill: color })] }) }) }));
});
export const LearnMoreDialogContent = () => {
    return (_jsxs(LearnMoreText, { children: [_jsx(VideoHintLink, { onClick: () => window.open("https://handbrake.fr/", "_blank"), children: "Handbrake" }), ",", " ", _jsx(VideoHintLink, { onClick: () => window.open("https://apps.apple.com/us/app/free-mp4-converter/id693443591?mt=12", "_blank"), children: "Free MP4 Converter" }), ", and", " ", _jsx(VideoHintLink, { onClick: () => window.open("https://apps.apple.com/us/app/mp4-video-converter/id889801314?mt=12", "_blank"), children: "MP4-Video Converter" }), " ", "are some examples of third-party websites and applications that can compress video files. Lowering the resolution, frame rate, or bitrate within any of these apps should reduce file size and allow you to add your video to the HealthEx informed consent experience."] }));
};
export const FileUploadProgress = observer((props) => {
    const { progress, disabled } = props;
    return (_jsxs(FileUploadingProgressContainer, { children: [_jsx(FileUploadingCircularProgress, { thickness: 4, value: progress, variant: "determinate", disabled: disabled }), _jsxs(FileUploadingProgressText, { disabled: disabled, children: [progress.toFixed(0), "%"] })] }));
});
